import { useState } from 'react';
import { Container, Typography, Box, Divider } from '@mui/material';
import HeroBanner from './HeroBanner';
import CustomButton from './CustomButton';

/**
 * Demo component showcasing all the customization options for HeroBanner
 * This component demonstrates:
 * - Button color customization
 * - Button text customization  
 * - Button width customization
 * - Mobile responsiveness
 * - Multiple button configurations
 */
export default function HeroBannerDemo() {
  const [currentDemo, setCurrentDemo] = useState('default');

  const demoConfigurations = {
    default: {
      title: "Default Hero Banner",
      subtitle: "This is the default configuration with customizable buttons.",
      primaryButton: {
        text: 'Get Started',
        color: '#121212',
        hoverColor: '#d5d5d5',
        textColor: 'white',
        hoverTextColor: '#000',
        width: '100%',
        show: true,
        onClick: () => alert('Primary button clicked!'),
      },
      secondaryButton: {
        text: 'Learn More',
        color: 'transparent',
        hoverColor: '#f0f0f0',
        textColor: '#555',
        hoverTextColor: '#000',
        width: '100%',
        show: true,
        variant: 'outlined',
        onClick: () => alert('Secondary button clicked!'),
      },
    },
    
    colorful: {
      title: "Colorful Buttons",
      subtitle: "Custom colors and gradients for a vibrant look.",
      backgroundColor: '#f0f8ff',
      primaryButton: {
        text: 'Start Your Journey',
        color: '#ff6b6b',
        hoverColor: '#ff5252',
        textColor: 'white',
        hoverTextColor: 'white',
        width: '100%',
        show: true,
        onClick: () => alert('Colorful primary button!'),
      },
      secondaryButton: {
        text: 'Discover More',
        color: '#4ecdc4',
        hoverColor: '#26a69a',
        textColor: 'white',
        hoverTextColor: 'white',
        width: '100%',
        show: true,
        onClick: () => alert('Colorful secondary button!'),
      },
    },
    
    widthVariations: {
      title: "Width Variations",
      subtitle: "Different button widths for various layouts.",
      primaryButton: {
        text: 'Full Width',
        color: '#2196f3',
        hoverColor: '#1976d2',
        textColor: 'white',
        hoverTextColor: 'white',
        width: '100%',
        show: true,
        onClick: () => alert('Full width button!'),
      },
      secondaryButton: {
        text: 'Half Width',
        color: '#ff9800',
        hoverColor: '#f57c00',
        textColor: 'white',
        hoverTextColor: 'white',
        width: '50%',
        show: true,
        onClick: () => alert('Half width button!'),
      },
    },
    
    gradient: {
      title: "Gradient Buttons",
      subtitle: "Beautiful gradient effects for modern designs.",
      backgroundColor: '#1a1a1a',
      primaryButton: {
        text: 'Gradient Primary',
        color: 'linear-gradient(45deg, #fe6b8b 30%, #ff8e53 90%)',
        hoverColor: 'linear-gradient(45deg, #ff8e53 30%, #fe6b8b 90%)',
        textColor: 'white',
        hoverTextColor: 'white',
        width: '100%',
        show: true,
        onClick: () => alert('Gradient primary button!'),
      },
      secondaryButton: {
        text: 'Gradient Secondary',
        color: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
        hoverColor: 'linear-gradient(45deg, #21cbf3 30%, #2196f3 90%)',
        textColor: 'white',
        hoverTextColor: 'white',
        width: '100%',
        show: true,
        onClick: () => alert('Gradient secondary button!'),
      },
    },
    
    minimal: {
      title: "Minimal Design",
      subtitle: "Clean and simple for professional layouts.",
      backgroundColor: '#fafafa',
      primaryButton: {
        text: 'Primary Action',
        color: '#000',
        hoverColor: '#333',
        textColor: 'white',
        hoverTextColor: 'white',
        width: '200px',
        show: true,
        onClick: () => alert('Minimal primary button!'),
      },
      secondaryButton: {
        text: 'Secondary',
        color: 'transparent',
        hoverColor: '#f5f5f5',
        textColor: '#666',
        hoverTextColor: '#333',
        width: '200px',
        show: true,
        variant: 'outlined',
        onClick: () => alert('Minimal secondary button!'),
      },
    },
  };

  const currentConfig = demoConfigurations[currentDemo];

  return (
    <>
      {/* Demo Controls */}
      <Box sx={{ 
        position: 'fixed', 
        top: 20, 
        right: 20, 
        zIndex: 2000,
        backgroundColor: 'white',
        p: 2,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        minWidth: '200px',
        '@media (max-width: 600px)': {
          position: 'static',
          m: 2,
          mb: 0,
        }
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>
          Demo Controls
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Object.keys(demoConfigurations).map((key) => (
            <CustomButton
              key={key}
              text={key.charAt(0).toUpperCase() + key.slice(1)}
              color={currentDemo === key ? '#2196f3' : '#f5f5f5'}
              textColor={currentDemo === key ? 'white' : '#333'}
              hoverColor={currentDemo === key ? '#1976d2' : '#e0e0e0'}
              hoverTextColor={currentDemo === key ? 'white' : '#000'}
              width="100%"
              padding="8px"
              fontSize="0.875rem"
              onClick={() => setCurrentDemo(key)}
              enableHoverScale={false}
            />
          ))}
        </Box>
      </Box>

      {/* Hero Banner Demo */}
      <HeroBanner
        {...currentConfig}
      >
        <Container maxWidth="sm" sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            This is a demo of the customizable hero banner component.
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="body2" sx={{ color: '#888', fontSize: '0.875rem' }}>
            Features demonstrated:
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', fontSize: '0.875rem' }}>
            ✓ Customizable button colors<br/>
            ✓ Customizable button text<br/>
            ✓ Customizable button width<br/>
            ✓ Mobile responsive design<br/>
            ✓ Gradient support<br/>
            ✓ Hover effects
          </Typography>
        </Container>
      </HeroBanner>

      {/* Code Example */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 20, 
        left: 20, 
        right: 20, 
        backgroundColor: '#1a1a1a',
        color: 'white',
        p: 2,
        borderRadius: 2,
        maxHeight: '200px',
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        '@media (max-width: 600px)': {
          position: 'static',
          m: 2,
          mt: 0,
        }
      }}>
        <Typography variant="body2" sx={{ color: '#4fc3f7', mb: 1 }}>
          Current Configuration:
        </Typography>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(currentConfig, null, 2)}
        </pre>
      </Box>
    </>
  );
} 